/**
 * Is this model failure worth retrying?
 *
 * Every article step shipped with `maxRetries = 0`, so a Vertex quota blip ended
 * the run permanently: of 11 failed article steps in production, the top three
 * read `failed after 0 retries: Resource exhausted` — a transient 429 that would
 * have cleared on a second attempt. The founder was sequencing articles by hand
 * to avoid it.
 *
 * `generate-plan-item.ts`, `generate-video.ts` and `render-target-cover.ts` each
 * classify this inline with their own slightly different substring list. This is
 * that rule in one place; those three predate it and still carry their own copy.
 */
const TRANSIENT_PATTERNS = [
  /resource exhausted/i,
  /\b429\b/,
  /rate[ _-]?limit/i,
  /quota/i,
  /timeout|timed out|etimedout/i,
  /\b50[234]\b/,
  /overloaded|unavailable|try again later/i,
  /econnreset|socket hang up|fetch failed/i,
];

/**
 * A malformed or empty model response. Distinct from a quota error: the call
 * reached the provider and came back unusable, which a fresh sample often fixes.
 */
const EMPTY_OR_MALFORMED = [
  /no output generated/i,
  /no object generated/i,
  /did not match schema/i,
  /structured output parse returned null/i,
];

export function isTransientModelError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    TRANSIENT_PATTERNS.some((p) => p.test(message)) ||
    EMPTY_OR_MALFORMED.some((p) => p.test(message))
  );
}

const STEP_FRAME = /Step\s+"[^"]*"\s+failed(?:\s+after\s+\d+\s+retries?)?:\s*/i;

/**
 * A model failure in words a person can act on. The durable-workflow runtime
 * wraps errors as `Step "step//./src/lib/..." failed after 2 retries: Resource
 * exhausted. Please refer to https://cloud.google.com/...`, and that string was
 * being stored verbatim as the reason a run stopped, then shown to the user.
 * Anything unrecognised is returned with the frame removed, never invented.
 */
export function describeModelFailure(error: unknown): string {
  const raw = (error instanceof Error ? error.message : String(error ?? '')).trim();
  const message = raw.replace(STEP_FRAME, '').trim();
  if (/resource exhausted|\b429\b|rate[ _-]?limit|quota/i.test(message))
    return 'the writing model was rate-limited';
  if (EMPTY_OR_MALFORMED.some((p) => p.test(message)))
    return 'the writing model returned no usable text';
  if (/timeout|timed out|etimedout/i.test(message)) return 'the writing model timed out';
  if (/ran out of room/i.test(message))
    return 'the writing model ran out of room before finishing the article';
  if (/\b50[234]\b|overloaded|unavailable/i.test(message))
    return 'the writing model was temporarily unavailable';
  return message.replace(/\.$/, '') || 'the writing model did not finish';
}

/** Quota errors deserve a longer pause than a malformed-response resample. */
export function retryDelayFor(error: unknown): number {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const quota = /resource exhausted|\b429\b|rate[ _-]?limit|quota/i.test(message);
  return quota ? 15_000 : 3_000;
}
